import * as Sentry from '@sentry/react'
import {
  useDebouncedCallback,
  useIsMobile,
  AlertDialog,
  SearchBar,
  useClientOptions,
  useDialogs,
  useUser,
  useUserPermissions
} from '@paintscout/ui'
import FileSaver from 'file-saver'
import { jsonToLucene } from 'json-to-lucene'
import type { ValueFilter } from 'json-to-lucene'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import { getFiltersFromLocation, type UrlQuery } from '../QuoteSearchBar'
import isEqual from 'lodash/isEqual'
import { gql } from 'apollo-boost'
import { useApolloClient } from '@apollo/react-hooks'
import ContactSearchFilters from './ContactSearchFilters'
import ContactSearchFilterChips from './ContactSearchFilterChips'

export interface ContactSearchBarProps {
  useUrl?: boolean
  loading?: boolean
  filters?: ValueFilter[]
  onSearch?: (query: string, filters: ValueFilter[], isInitialSearch?: boolean) => any
}

function ContactSearchBar({ loading, useUrl = true, onSearch, ...props }: ContactSearchBarProps) {
  const { permissionToCreateContact } = useUserPermissions()
  const { openDialog, dismissDialog } = useDialogs()
  const apolloClient = useApolloClient()
  const { options } = useClientOptions()
  const location = useLocation()
  const { isSales } = useUser()
  const mobile = useIsMobile()

  const [queryText, setQueryText] = useState((useUrl && getUrlQuery<UrlQuery>({ location }).search) || '')
  const [showFilters, setShowFilters] = useState(false)
  const [exporting, setExporting] = useState(false)
  // the committed/current filters for the results
  const [filters, setFilters] = useState(props.filters ?? getFiltersFromLocation(location))
  // filters state before the user commits them by hitting search
  const [uncommittedFilters, setUncommittedFilters] = useState(filters)

  const dateFormat = options.options.dateFormat.momentValue
  const placeholder = `Search by name, email, address, or company...`

  // trigger search when props.filters changes
  useEffect(() => {
    if (!isEqual(filters, props.filters)) {
      handleSearch({ filters: props.filters })
    }
    // eslint-disable-next-line
  }, [props.filters])

  // trigger search on mount
  useEffect(() => {
    handleSearch({ filters, query: queryText }, true)
  }, [])

  const handleSearch = (args: { query?: string; filters?: ValueFilter[] } = {}, isInitialSearch?: boolean) => {
    const { query = queryText } = args
    const strippedQuery = query?.startsWith('@') ? query.slice(1) : query

    // the filter values (`filters` variable from state is in scope, so we'll call it values)
    const values = [...(args.filters ?? uncommittedFilters)]
    // update filter states
    setFilters(values)
    setUncommittedFilters(values)
    // update the typed-in user text
    setQueryText(strippedQuery)

    // setQuery(args.query)
    // remove filters with null or undefined values
    const sanitizedValues = values.filter((filter) => {
      // != operator rather than !== is intentional - it checks for null or undefined - update to catch empty arrays
      return (
        filter.value != null &&
        (!Array.isArray(filter.value) || (Array.isArray(filter.value) && filter.value.filter(Boolean).length))
      )
    })

    if (useUrl) {
      const { page } = getUrlQuery<UrlQuery>({ location })

      setUrlQuery({
        location,
        params: { search: query || null, page: page || undefined, filters: sanitizedValues },
        replaceParams: true
      })
    }
    onSearch(strippedQuery, sanitizedValues, isInitialSearch)
  }

  const debouncedSearch = useDebouncedCallback(handleSearch, 100)

  const handleFilterClear = () => {
    setUncommittedFilters([])
  }

  const handleExport = async () => {
    const dateFilter = uncommittedFilters.find((filter) => filter.id === 'created_date')
    const fileName = dateFilter
      ? // "Contact 01/01/2000 - 02/01/2000"
        `Contact ${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(dateFilter.value[1]).format(
          dateFormat
        )}.csv`
      : // "Contact <today's date>"
        `Contact ${moment(Date.now()).format(dateFormat)}.csv`

    setExporting(true)
    try {
      // use uncommitted filters since search box will still be open
      const withTrashedFilter: ValueFilter[] = [
        ...uncommittedFilters,
        { type: 'equal', id: 'trashed', value: true, not: true }
      ]
      const luceneQuery = jsonToLucene({
        query: queryText,
        filters: withTrashedFilter
      })

      // const sort = queryText.length && queryText !== '*:*' ? '-<score>' : '-updated_date' // Manually set here so is same as quoteList
      const sanitizedQuery = (luceneQuery ? luceneQuery : '*:*').replace(/\s*AND\s*\(\)/g, '')
      const sort = [getUrlQuery<UrlQuery>({ location })?.sort ?? '-created_date', '-updated_date']
      const { data } = await apolloClient.query({
        query: gql`
          query ($query: String!, $sort: [String!]) {
            exportContacts(query: $query, sort: $sort) {
              csv
              rows
            }
          }
        `,
        variables: {
          query: sanitizedQuery,
          sort
        }
      })

      if (data.exportContacts.rows) {
        FileSaver.saveAs(new Blob([data.exportContacts.csv]), fileName)
      } else {
        openDialog(AlertDialog, {
          message: 'No rows were found to export for this search query.',
          onConfirm: () => {
            dismissDialog()
          }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    setExporting(false)
  }

  function deleteFilter({
    id,
    valueIndex,
    valueLabel
  }: {
    id: string
    valueIndex?: number | string
    valueLabel?: string
  }) {
    if (
      (Number(valueIndex) >= 0 || valueLabel.length) &&
      (id === 'owner.id' || id === 'tags' || id === 'preferred_contact_method' || id === 'lead_source')
    ) {
      handleSearch({
        filters: filters
          .map((filter) => {
            if (filter.id === id && filter?.value) {
              if (filter.type === 'in' || filter.id === 'tags') {
                return {
                  ...filter,
                  value: (filter.value as any).filter((value, index) => {
                    if (valueLabel && valueLabel === value) {
                      return false
                    } else if (index === valueIndex) {
                      return false
                    } else if (valueIndex === value) {
                      return false
                    }
                    return true
                  })
                }
              } else {
                return null
              }
            } else {
              return filter
            }
          })
          .filter(Boolean)
      })
    } else {
      // Remove the only filter of type
      handleSearch({
        filters: filters.filter((filter) => {
          return filter.id !== id
        })
      })
    }
  }

  return (
    <div>
      <SearchBar
        inputProps={{
          id: 'contact-search',
          'aria-label': 'Search Contacts',
          // recommended aria for loading indicator (also used in e2e tests)
          'aria-busy': loading,
          'aria-live': loading ? 'polite' : undefined
        }}
        filterMenuCollapse={!mobile}
        defaultValue={queryText}
        showExport={permissionToCreateContact && !isSales}
        placeholder={placeholder}
        isExporting={exporting}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        onClear={handleFilterClear}
        filterContent={<ContactSearchFilters filters={uncommittedFilters} onChange={setUncommittedFilters} />}
        onExport={handleExport}
        onChange={(q) => {
          const cleanQuery = q.replace(/\\/g, '/')
          debouncedSearch({ query: cleanQuery })
        }}
        onSearch={(q) => handleSearch({ query: q })}
        {...props}
      />
      <ContactSearchFilterChips
        show={(filters ?? []).length > 0 && !showFilters}
        filters={filters}
        onDelete={({ filter, valueIndex, valueLabel }) => deleteFilter({ id: filter.id, valueIndex, valueLabel })}
      />
    </div>
  )
}

export default ContactSearchBar
